import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Card } from '@/components/atoms/Card';
import { MARKET_TEXTBOX_OPTIONAL_LIST } from '@/pages/Market/_constants/const';
import TextField from '@/components/molecules/TextField';
import PwSeeIcon from '@/assets/icons/PwSeeIcon.svg?react';
import PwHideIcon from '@/assets/icons/PwHideIcon.svg?react';
import { CPLAT_API_ROUTES, usePost } from '@/api';
import { useAppSelector } from '@/redux/store';
import { ROUTES } from '@/routes';
import { Dialog, DialogContent } from '@/components/molecules/Dialog';
import { Button } from '@/components/atoms/PcButton';

import { TermsFormData } from '@/types/market';
import { useLog } from '@/hooks/V3/useLog';
import Input from '@/components/atoms/Input';
import InquiryButton from '@/components/organisms/InquiryButton';

type FormData = {
  marketName: string;
  marketId: string;
  marketPassword: string;
  agreementList: TermsFormData[];
};

const AGREEMENT_LIST: TermsFormData[] = [
  {
    Key: 'extra-guide',
    GuideMessage: '[필수] 계정 연동 권한 동의',
    ReferenceMessage: '동의 내용 보기',
    ReferenceUrl: 'https://www.cplat.io/dccae533-9a8a-4333-8fbe-85019be04c72',
    IsInputMandatory: true,
    values: true,
  },
];

const ReqAddMarketContainer = () => {
  const { register, watch, setError, formState, setValue, handleSubmit } = useForm<FormData>({
    defaultValues: {
      marketName: '',
      marketId: '',
      marketPassword: '',
      agreementList: AGREEMENT_LIST,
    },
  });
  const { errors } = formState;
  const formValues = watch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const { CplatToken, UserNo } = user;
  const { setUserEventHistory } = useLog(UserNo);

  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [hideList, setHideList] = useState<{ [key: string]: boolean }>({});

  const { mutate: marketRegister, isPending } = usePost(CPLAT_API_ROUTES.registerMarketAccount, {});

  const inputList = [
    {
      hasToBeEncrypted: false,
      viewEncryptIcon: false,
      placeholder: '마켓 이름을 입력하세요',
      key: 'marketName',
    },
    {
      hasToBeEncrypted: false,
      viewEncryptIcon: false,
      placeholder: '아이디를 입력하세요',
      key: 'marketId',
    },
    {
      hasToBeEncrypted: true,
      viewEncryptIcon: true,
      placeholder: '비밀번호를 입력하세요',
      key: 'marketPassword',
    },
  ];

  const handleCheck = (key: string, checked: boolean) => {
    setValue(
      'agreementList',
      formValues.agreementList.map((agreement) =>
        agreement.Key === key ? { ...agreement, values: checked } : agreement,
      ),
    );
  };

  const isDisable =
    [formValues.marketName, formValues.marketId, formValues.marketPassword].some((v) => !v) ||
    formValues.agreementList.some(({ values, IsInputMandatory }) => IsInputMandatory && !values);

  const onSubmit = () => {
    const body = {
      CplatToken,
      MarketName: 'extra',
      MarketLoginInfo: JSON.stringify({
        MarketName: 'extra',
        MarketId: formValues.marketId,
        MarketPassword: formValues.marketPassword,
        VendorId: formValues.marketName,
        AgreementList: formValues.agreementList,
      }),
    };

    marketRegister(body, {
      onSuccess(res) {
        const { code, message } = res.data as { code: string; message: string };
        if (code === '200') {
          setIsCompleteModalOpen(true);
          setUserEventHistory({
            eventName: 'request_market_add',
            eventValue: formValues.marketName,
          });
        } else if (code === '611') {
          setError('root', { message });
        }
      },
    });
  };

  return (
    <div className="flex flex-col gap-[20px] rounded-[20px] pl-[6px]">
      <Card className="px-[20px] py-[24px]">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <h2 className="text-gr-900 Body1S16">마켓 추가 요청</h2>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="relative flex flex-col gap-4">
              {inputList.map(({ hasToBeEncrypted, placeholder, key }) => {
                const label = MARKET_TEXTBOX_OPTIONAL_LIST[key]?.title || key;
                return (
                  <div key={key} className={`flex items-center gap-4`}>
                    <label className="w-[70px] text-gr-800 Body2M16">
                      {label}
                      <span className="ml-1 text-red-400">*</span>
                    </label>
                    <div className="flex-1">
                      {hasToBeEncrypted ? (
                        <TextField
                          {...register(key)}
                          placeholder={placeholder}
                          type={hideList[key] ? 'password' : 'text'}
                          variant="register"
                          className="Body5M14"
                          InputProps={{
                            endAdornment: hideList[key] ? (
                              <PwHideIcon
                                onClick={() => setHideList({ ...hideList, [key]: !hideList[key] })}
                                className="cursor-pointer"
                              />
                            ) : (
                              <PwSeeIcon
                                onClick={() => setHideList({ ...hideList, [key]: !hideList[key] })}
                                className="cursor-pointer"
                              />
                            ),
                          }}
                        />
                      ) : (
                        <Input
                          {...register(key)}
                          placeholder={placeholder}
                          variant="register"
                          className="Body5M14"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
              {errors.root?.message && (
                <p className="text-red-600 Caption1M12">{errors.root.message}</p>
              )}
            </div>
            <div className="mt-4 flex flex-col gap-4">
              {formValues.agreementList.map(({ Key, values }) => (
                <div key={Key} className="flex items-center justify-center space-x-[8px]">
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer rounded-[2px] border-gr-300 accent-point-500"
                    checked={values === true}
                    onChange={(e) => handleCheck(Key, e.target.checked)}
                  />
                  <p className="text-gr-700 Body5M14">[필수] 계정 연동 권한 동의</p>
                  <a
                    className="cursor-pointer align-bottom text-gr-600 underline Body5M14"
                    href="https://sellerbox.notion.site/db71e932abda4ab1bc23cb51113533e9"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    전문보기
                  </a>
                </div>
              ))}
              <div className="flex items-center justify-center">
                <Button
                  type="submit"
                  className="h-[44px] w-[300px] rounded-[16px] Body5M14"
                  disabled={isDisable || isPending}
                >
                  연동하기
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Card>
      <InquiryButton className="" />

      <Dialog open={isCompleteModalOpen}>
        <DialogContent className="flex !w-[380px] flex-col items-center p-[30px] pb-[20px] text-center">
          <h3 className="text-gr-900 Title3S18">마켓 추가 요청이 완료되었습니다!</h3>
          <p className="text-gr-700 Body2M16">
            셀러박스에서 빠른 시일 내에 연동을
            <br />
            진행할 수 있도록 노력하겠습니다!
          </p>
          <Button
            onClick={() => {
              setIsCompleteModalOpen(false);
              navigate(ROUTES.market);
            }}
            variant="secondary"
            className="mt-[10px] w-[200px]"
          >
            확인
          </Button>
        </DialogContent>
      </Dialog>
      {/* <Dialog open={isTermsModalOpen} onOpenChange={() => setIsTermsModalOpen(!isTermsModalOpen)}>
        <DialogContent className="flex h-[500px] !w-[800px] flex-col items-center !p-0  text-center">
          <div
            className="flex w-full cursor-pointer justify-end p-[20px]"
            onClick={() => setIsTermsModalOpen(false)}
          >
            <X />
          </div>
          <iframe
            src="https://sellerbox.notion.site/db71e932abda4ab1bc23cb51113533e9"
            className="h-[100%] w-[100%] rounded-[16px]"
          />
        </DialogContent>
      </Dialog> */}
    </div>
  );
};

export default ReqAddMarketContainer;
